import { FORWARD_TURRET_RANGE_SQ } from '../../data/forwardTurret.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { MiniTurret } from '../entities/Building.js';
import type { ControlNode } from '../entities/ControlNode.js';
import type { PlayerId } from '../ids.js';

// V1.5+ forward turret network dependency per docs/02_GAME_DESIGN.md §15.
//
// For each `mini_turret`, derive `isOnline` from "is there an
// owned-by-the-turret's-owner ControlNode marked `isConnected: true` within
// FORWARD_TURRET_RANGE_PX of the turret?". CombatSystem and FogSystem early-
// out when `isOnline === false`. Permanent buildings (`production_hub`,
// `turret`) are kept always-on — Core-ring placement makes them inherently
// network-rooted (§15 "Core-adjacent defensive turrets always work"). The
// system reasserts `true` for permanent kinds defensively so a future refactor
// can't leave the field stale.
//
// Why a separate system rather than inlining the check in CombatSystem:
//   - The `isOnline` flag is read by MULTIPLE systems (combat, fog, renderer,
//     minimap, sfx-edge-detector, bot threat assessment) — single point of
//     truth keeps them consistent.
//   - The proximity scan walks every owned connected node × every
//     mini_turret. Pre-bucketing by owner once per tick lets every reader
//     consume the boolean without re-running the scan.
//   - SFX is an edge-detected concern (online → offline transition); a
//     system that owns the field cleanly emits the rising/falling edges via
//     diffing on the client snapshot.
//
// Ordering (Simulation.tick): runs immediately after SignalLinkSystem (which
// sets node.isConnected) so the proximity check reads the up-to-date
// connectivity. CombatSystem ran earlier in the tick, so the offline gate
// becomes visible to combat on the NEXT tick — at most a 50 ms (one tick)
// lag from "relay flip" to "turret stops firing", well inside the §15
// "feels like a flip cuts the turret" budget. FogSystem runs LATER in the
// same tick so vision gating is same-tick.
//
// Determinism: pure function of state. No allocations beyond a small
// per-call working array. No RNG (Rule 8 satisfied automatically).
export function tickForwardTurretNetwork(state: MatchState): void {
  const ownedConnected = collectOwnedConnectedNodes(state);

  for (const b of Object.values(state.buildings)) {
    if (b.kind !== 'mini_turret') {
      // Permanent buildings stay always-on — their Core-ring placement is the
      // network anchor (§15). Defensive reassertion in case a future refactor
      // leaves the field stale.
      b.isOnline = true;
      continue;
    }
    b.isOnline = hasOwnedConnectedNodeInRange(b, ownedConnected[b.ownerId]);
    if (!b.isOnline) {
      // Telemetry: same shape as SignalLinkSystem's
      // isolatedRelaySecondsByPlayer counter — accumulates per-tick seconds
      // so the playtest summary surfaces "how long was forward pressure
      // network-disrupted?" per side.
      state.stats.forwardTurretOfflineSecondsByPlayer[b.ownerId] += SECONDS_PER_TICK;
    }
  }
}

type OwnedConnectedNodes = Record<PlayerId, ControlNode[]>;

function collectOwnedConnectedNodes(state: MatchState): OwnedConnectedNodes {
  const out: OwnedConnectedNodes = { 0: [], 1: [] };
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === null) continue;
    if (!node.isConnected) continue;
    out[node.ownerId].push(node);
  }
  return out;
}

function hasOwnedConnectedNodeInRange(
  building: MiniTurret,
  candidates: ControlNode[],
): boolean {
  const bx = building.pos.x;
  const by = building.pos.y;
  for (const n of candidates) {
    const dx = n.pos.x - bx;
    const dy = n.pos.y - by;
    if (dx * dx + dy * dy <= FORWARD_TURRET_RANGE_SQ) return true;
  }
  return false;
}
